import { useAuthUser } from '@/hooks/useAuthUser';
import useCurrentUser from '@/hooks/useCurrentUser';
import OnboardingLayout from '@/layouts/OnboardingLayout';
import { firebaseAuth } from '@/lib/Firebase';
import { getSafeInternalReturnUrl } from '@/utils/safeReturnUrl';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';
import EnterPhone from './EnterPhone';
import VerifyPhone from './VerifyPhone';

function Login<NextPageWithLayout>() {
  const router = useRouter();
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [step, setStep] = useState<number>(0);
  const [newUser, setIsNewUser] = useState<boolean | null>(null);
  const [isVerified, setIsVerified] = useState(false);
  const { isLoading, data: authUser } = useAuthUser(['authUser'], firebaseAuth);
  const {
    refetch,
    data: user,
    isLoading: isUserLoading,
    isFetching: isUserFetching,
    isError: isUserError,
    isFetched: isUserFetched,
  } = useCurrentUser(!!authUser);

  useEffect(() => {
    if (!router.isReady || !authUser || !isUserFetched || isUserLoading || isUserFetching) {
      return;
    }

    if (isUserError) {
      void firebaseAuth.signOut();
      return;
    }

    const safeReturn = getSafeInternalReturnUrl(router.query.returnUrl);
    if (safeReturn) {
      router.replace(safeReturn);
      return;
    }

    if (!user) {
      router.replace('/onboarding');
      return;
    }
    if (!user.isVerifiedStudent) {
      router.replace('/onboarding');
      return;
    }
    router.replace(user.canShowTradingFeatures ? '/wallet' : '/');
  }, [
    router.isReady,
    authUser,
    isUserFetched,
    isUserLoading,
    isUserFetching,
    isUserError,
    user,
    router,
    router.query.returnUrl,
  ]);

  useEffect(() => {
    const isNewUser = localStorage?.getItem('isNewUser') === 'true';
    if (!!isNewUser) setIsNewUser(isNewUser);
  }, []);

  const onChangePhone = (phone: string) => {
    setPhoneNumber(phone);
  };

  const onVerified = async (verified, isNewUser) => {
    setIsVerified(verified);
    await refetch();
  };

  const onNext = useCallback(() => {
    setStep(step + 1);
  }, [step]);

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <EnterPhone onNext={onNext} phoneNumber={phoneNumber} onChangePhone={onChangePhone} />
        );
      default:
        return <VerifyPhone phoneNumber={phoneNumber} onVerified={onVerified} />;
    }
  };

  return (
    <div className="bg-linear-primary w-full h-screen flex flex-col place-content-center gap-8 p-8">
      <div className="flex-none">
        <Link href="/">
          <Image
            src="/images/bloom-landscape-white-logo.svg"
            alt="Bloom logo"
            className="mx-auto"
            width={170}
            height={48}
            priority
          />
        </Link>
      </div>
      <div className="flex-1 h-full flex flex-row place-content-center">
        {isLoading || authUser ? (
          <div className="flex flex-col place-content-center w-full">
            <span className="flex h-12 w-12 relative mx-auto my-auto">
              <span className="animate-ping absolute inline-flex size-12 rounded-full bg-white/50 opacity-75">
                <span className="animate-ping absolute inline-flex size-12 rounded-full bg-white/50 opacity-75">
                  <span className="animate-ping absolute inline-flex size-12 rounded-full bg-white opacity-75 z-10"></span>
                </span>
              </span>
            </span>
          </div>
        ) : (
          renderStep()
        )}
      </div>
    </div>
  );
}

export default Login;

//Login.getLayout = OnboardingLayout;
